import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Property data
  properties: [],
  filteredProperties: [],
  visibleProperties: [],
  
  // Loading states
  isLoading: false,
  isPreloading: false,
  isRefreshing: false,
  
  // Cache management
  lastFetchTime: null,
  cacheExpiry: 5 * 60 * 1000, // 5 minutes in milliseconds
  
  // Pagination
  currentPage: 0,
  itemsPerBatch: 8,
  hasMoreProperties: true,
  
  // Search and filter state
  currentSearchResults: null,
  currentFilters: null,
  currentSortType: "default",
  
  // Error handling
  error: null,
  
  // Performance tracking
  fetchCount: 0,
  lastPerformanceMetrics: null,
};

export const RentalPropertySlice = createSlice({
  name: "rentalProperties",
  initialState,
  reducers: {
    // Pre-loading actions
    startPreloading: (state) => {
      state.isPreloading = true;
      state.error = null;
    },
    
    preloadPropertiesSuccess: (state, action) => {
      const { properties, timestamp } = action.payload;
      state.properties = properties;
      state.filteredProperties = properties.filter(property => 
        property?.short?.islisted === true
      );
      state.visibleProperties = state.filteredProperties.slice(0, state.itemsPerBatch);
      state.lastFetchTime = timestamp;
      state.isPreloading = false;
      state.error = null;
      state.fetchCount += 1;
      state.hasMoreProperties = state.filteredProperties.length > state.itemsPerBatch;
    },
    
    preloadPropertiesFailure: (state, action) => {
      state.isPreloading = false;
      state.error = action.payload;
    },
    
    // Regular loading actions
    startLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    
    loadPropertiesSuccess: (state, action) => {
      const { properties, timestamp, isRefresh = false } = action.payload;
      
      if (isRefresh) {
        // For refresh, replace all properties
        state.properties = properties;
        state.filteredProperties = properties.filter(property => 
          property?.short?.islisted === true
        );
      } else {
        // For regular load, merge with existing properties
        const existingIds = new Set(state.properties.map(p => p.token_id));
        const newProperties = properties.filter(p => !existingIds.has(p.token_id));
        
        state.properties = [...state.properties, ...newProperties];
        state.filteredProperties = state.properties.filter(property => 
          property?.short?.islisted === true
        );
      }
      
      state.visibleProperties = state.filteredProperties.slice(0, state.itemsPerBatch);
      state.lastFetchTime = timestamp;
      state.isLoading = false;
      state.isRefreshing = false;
      state.error = null;
      state.fetchCount += 1;
      state.hasMoreProperties = state.filteredProperties.length > state.itemsPerBatch;
    },
    
    loadPropertiesFailure: (state, action) => {
      state.isLoading = false;
      state.isRefreshing = false;
      state.error = action.payload;
    },
    
    // Property filtering and search
    setFilteredProperties: (state, action) => {
      state.filteredProperties = action.payload;
      state.visibleProperties = action.payload.slice(0, state.itemsPerBatch);
      state.hasMoreProperties = action.payload.length > state.itemsPerBatch;
    },
    
    setSearchResults: (state, action) => {
      state.currentSearchResults = action.payload;
      if (action.payload && action.payload.length > 0) {
        state.filteredProperties = action.payload;
        state.visibleProperties = action.payload.slice(0, state.itemsPerBatch);
      } else {
        // Handle empty search results - clear filtered properties
        state.filteredProperties = [];
        state.visibleProperties = [];
        state.hasMoreProperties = false;
      }
    },
    
    clearSearchResults: (state) => {
      state.currentSearchResults = null;
      state.filteredProperties = state.properties.filter(property => 
        property?.short?.islisted === true
      );
      state.visibleProperties = state.filteredProperties.slice(0, state.itemsPerBatch);
    },
    
    // Pagination
    loadMoreProperties: (state) => {
      const currentLength = state.visibleProperties.length;
      const nextBatch = state.filteredProperties.slice(
        currentLength,
        currentLength + state.itemsPerBatch
      );
      state.visibleProperties = [...state.visibleProperties, ...nextBatch];
      state.hasMoreProperties = state.visibleProperties.length < state.filteredProperties.length;
    },
    
    resetPagination: (state) => {
      state.visibleProperties = state.filteredProperties.slice(0, state.itemsPerBatch);
      state.hasMoreProperties = state.filteredProperties.length > state.itemsPerBatch;
      state.currentPage = 0;
    },
    
    // Cache management
    invalidateCache: (state) => {
      state.lastFetchTime = null;
      state.properties = [];
      state.filteredProperties = [];
      state.visibleProperties = [];
    },
    
    refreshProperties: (state) => {
      state.isRefreshing = true;
      state.error = null;
    },
    
    // Performance tracking
    updatePerformanceMetrics: (state, action) => {
      state.lastPerformanceMetrics = {
        ...action.payload,
        timestamp: Date.now(),
      };
    },
    
    // Utility actions
    setSortType: (state, action) => {
      state.currentSortType = action.payload;
    },
    
    setCurrentFilters: (state, action) => {
      state.currentFilters = action.payload;
    },
    
    // Reset state
    resetRentalProperties: () => initialState,
  },
});

export const {
  startPreloading,
  preloadPropertiesSuccess,
  preloadPropertiesFailure,
  startLoading,
  loadPropertiesSuccess,
  loadPropertiesFailure,
  setFilteredProperties,
  setSearchResults,
  clearSearchResults,
  loadMoreProperties,
  resetPagination,
  invalidateCache,
  refreshProperties,
  updatePerformanceMetrics,
  setSortType,
  setCurrentFilters,
  resetRentalProperties,
} = RentalPropertySlice.actions;

// Selectors
export const selectAllProperties = (state) => state.rentalProperties.properties;
export const selectFilteredProperties = (state) => state.rentalProperties.filteredProperties;
export const selectVisibleProperties = (state) => state.rentalProperties.visibleProperties;
export const selectIsLoading = (state) => state.rentalProperties.isLoading;
export const selectIsPreloading = (state) => state.rentalProperties.isPreloading;
export const selectIsRefreshing = (state) => state.rentalProperties.isRefreshing;
export const selectHasMoreProperties = (state) => state.rentalProperties.hasMoreProperties;
export const selectLastFetchTime = (state) => state.rentalProperties.lastFetchTime;
export const selectCacheExpiry = (state) => state.rentalProperties.cacheExpiry;
export const selectError = (state) => state.rentalProperties.error;
export const selectCurrentSortType = (state) => state.rentalProperties.currentSortType;
export const selectCurrentFilters = (state) => state.rentalProperties.currentFilters;

// Cache validation selector
export const selectIsCacheValid = (state) => {
  const { lastFetchTime, cacheExpiry } = state.rentalProperties;
  if (!lastFetchTime) return false;
  return (Date.now() - lastFetchTime) < cacheExpiry;
};

// Property count selectors
export const selectTotalPropertyCount = (state) => state.rentalProperties.properties.length;
export const selectFilteredPropertyCount = (state) => state.rentalProperties.filteredProperties.length;
export const selectVisiblePropertyCount = (state) => state.rentalProperties.visibleProperties.length;

export default RentalPropertySlice.reducer;
